import React from "react"
import Grid from '@material-ui/core/Grid'
import { makeStyles } from '@material-ui/core/styles'
import { Link } from "gatsby"

import SEO from "../components/seo"
import Layout from '../components/layout'
import '../CSS/WellnessTools.css'

const useStyles = makeStyles(() => ({
  root: { flexGrow: 1 },
}))

const PATTERNS = {
  box:  { label: 'Box Breathing',   phases: ['Inhale', 'Hold', 'Exhale', 'Hold'],  durations: [4, 4, 4, 4], description: '4–4–4–4 · Best for focus & calming anxiety' },
  calm: { label: 'Simple Calm',     phases: ['Inhale', 'Exhale'],                  durations: [4, 6],        description: '4–6 · Quick, easy stress relief' },
  p478: { label: '4-7-8 Breathing', phases: ['Inhale', 'Hold', 'Exhale'],          durations: [4, 7, 8],     description: '4–7–8 · Deep relaxation & sleep aid' },
}

const SESSION_OPTIONS = [
  { label: '1 min',  seconds: 60  },
  { label: '2 min',  seconds: 120 },
  { label: '5 min',  seconds: 300 },
]

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

/* Full-page gentle orb drift — activates when breathing session is running */
function BreathingFullPage() {
  const orbs = [
    { size:18, left:'5%',  bottom:'15%', dur:'8s',  delay:'0s'   },
    { size:12, left:'15%', bottom:'8%',  dur:'10s', delay:'1.5s' },
    { size:22, left:'28%', bottom:'20%', dur:'7s',  delay:'3s'   },
    { size:10, left:'42%', bottom:'5%',  dur:'9s',  delay:'0.7s' },
    { size:16, left:'56%', bottom:'12%', dur:'11s', delay:'4s'   },
    { size:14, left:'68%', bottom:'18%', dur:'8.5s',delay:'2s'   },
    { size:20, left:'78%', bottom:'7%',  dur:'6.5s',delay:'5s'   },
    { size:11, left:'88%', bottom:'22%', dur:'9.5s',delay:'1s'   },
    { size:16, left:'94%', bottom:'10%', dur:'7.5s',delay:'3.5s' },
    { size:13, left:'35%', bottom:'30%', dur:'12s', delay:'2.8s' },
  ]
  return (
    <div className="fp-overlay">
      {orbs.map((o, i) => (
        <div key={i} className="fp-breath-orb" style={{
          width:o.size, height:o.size,
          left:o.left, bottom:o.bottom,
          animationDuration:o.dur, animationDelay:o.delay,
        }} />
      ))}
    </div>
  )
}

export default function BreathingGuide() {
  const classes = useStyles()

  const [patternKey, setPatternKey]   = React.useState('box')
  const [sessionSecs, setSessionSecs] = React.useState(120)
  const [isActive, setIsActive]       = React.useState(false)
  const [isComplete, setIsComplete]   = React.useState(false)
  const [phaseIndex, setPhaseIndex]   = React.useState(0)
  const [countdown, setCountdown]     = React.useState(0)
  const [elapsed, setElapsed]         = React.useState(0)

  const pattern = PATTERNS[patternKey]

  /* Determine circle CSS class based on phase */
  const getCircleClass = () => {
    if (!isActive) return ''
    const phase = pattern.phases[phaseIndex]
    if (phase === 'Inhale') return 'inhale'
    if (phase === 'Exhale') return 'exhale'
    /* Two Hold phases: first is hold (expanded), second is hold2 (contracted) */
    const prevPhase = phaseIndex > 0 ? pattern.phases[phaseIndex - 1] : null
    return prevPhase === 'Inhale' ? 'hold' : 'hold2'
  }

  /* Session elapsed timer — ends session when time runs out */
  React.useEffect(() => {
    if (!isActive) return
    const t = setInterval(() => {
      setElapsed(prev => {
        if (prev + 1 >= sessionSecs) {
          setIsActive(false)
          setIsComplete(true)
          return sessionSecs
        }
        return prev + 1
      })
    }, 1000)
    return () => clearInterval(t)
  }, [isActive, sessionSecs])

  /* Phase countdown — advances to next phase when countdown hits 0 */
  React.useEffect(() => {
    if (!isActive) return
    const dur = pattern.durations[phaseIndex]
    setCountdown(dur)
    const t = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          setPhaseIndex(pi => (pi + 1) % pattern.phases.length)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(t)
  }, [isActive, phaseIndex, patternKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleStart = () => {
    setIsComplete(false)
    setElapsed(0)
    setPhaseIndex(0)
    setCountdown(pattern.durations[0])
    setIsActive(true)
  }

  const handlePause = () => setIsActive(false)

  const handleReset = () => {
    setIsActive(false)
    setIsComplete(false)
    setElapsed(0)
    setPhaseIndex(0)
    setCountdown(0)
  }

  const handlePatternChange = (key) => {
    setPatternKey(key)
    handleReset()
  }

  const progressPct = Math.round((elapsed / sessionSecs) * 100)
  const currentPhaseLabel = pattern.phases[phaseIndex] || 'Inhale'

  return (
    <Layout>
      {/* Full-page gentle orb drift when session is active */}
      {isActive && <BreathingFullPage />}
      <SEO
        title="Breathing Guide | Pratiiti — Calm Your Mind"
        description="Practice guided breathing exercises with Pratiiti. Choose Box Breathing, 4-7-8, or Simple Calm to reduce stress and restore focus in minutes."
        canonical="https://www.pratiiti.in/breathing-guide"
      />

      <div className={classes.root}>
        <Grid container justify="center">
          <Grid item xs={12} sm={10} md={8}>
            <div className="wellness-page-wrapper">

              {/* Page Heading — matches existing .pageheading style */}
              <div className="pageheading">Breathing Guide</div>
              <p className="wellness-section-subtitle">
                Conscious breathing is one of the most effective tools for calming your nervous system.
                Choose a pattern below, set a duration, and breathe.
              </p>

              <div className="wellness-card">

                {isComplete ? (
                  /* ── Completion Screen ── */
                  <div className="breathing-complete-msg">
                    <div style={{ fontSize: 56, marginBottom: 16 }}>🌿</div>
                    <h3>Well done.</h3>
                    <p>Take a moment before you move on.<br />Your body and mind thank you for that.</p>
                    <div className="wellness-btn-group">
                      <button className="wellness-btn wellness-btn-primary" onClick={handleReset}>
                        Breathe Again
                      </button>
                      <Link to="/guided-visualization">
                        <button className="wellness-btn">Try Guided Visualisation</button>
                      </Link>
                    </div>
                  </div>

                ) : (
                  <>
                    {/* ── Pattern Selector (only shown when not active) ── */}
                    {!isActive && (
                      <>
                        <p style={{ textAlign: 'center', color: '#555', fontSize: 14, marginBottom: 10 }}>
                          Choose a breathing pattern:
                        </p>
                        <div className="breathing-pattern-selector">
                          {Object.entries(PATTERNS).map(([key, p]) => (
                            <button
                              key={key}
                              className={`breathing-pattern-btn${patternKey === key ? ' active' : ''}`}
                              onClick={() => handlePatternChange(key)}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                        <p style={{ textAlign: 'center', color: '#888', fontSize: 13, marginBottom: 20 }}>
                          {pattern.description}
                        </p>

                        <p style={{ textAlign: 'center', color: '#555', fontSize: 14, marginBottom: 10 }}>
                          Session duration:
                        </p>
                        <div className="breathing-duration-selector">
                          {SESSION_OPTIONS.map(opt => (
                            <button
                              key={opt.seconds}
                              className={`breathing-duration-btn${sessionSecs === opt.seconds ? ' active' : ''}`}
                              onClick={() => setSessionSecs(opt.seconds)}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}

                    {/* ── Animated Breathing Scene ── */}
                    <div className="breathing-scene-wrapper">
                      {/* Floating orbs — staggered delays give a gentle bubbling feel */}
                      <div className="b-orb" style={{ width:10, height:10, left:'12%', bottom:'18%', animationDuration:'6s',   animationDelay:'0s'    }} />
                      <div className="b-orb" style={{ width: 7, height: 7,  left:'22%', bottom:'8%',  animationDuration:'8s',   animationDelay:'1.2s'  }} />
                      <div className="b-orb" style={{ width:13, height:13, left:'68%', bottom:'14%', animationDuration:'7s',   animationDelay:'2.5s'  }} />
                      <div className="b-orb" style={{ width: 9, height: 9,  left:'80%', bottom:'24%', animationDuration:'5s',   animationDelay:'0.7s'  }} />
                      <div className="b-orb" style={{ width: 6, height: 6,  left:'44%', bottom:'4%',  animationDuration:'9s',   animationDelay:'3.1s'  }} />
                      <div className="b-orb" style={{ width:11, height:11, left:'58%', bottom:'32%', animationDuration:'6.5s', animationDelay:'1.8s'  }} />
                      <div className="b-orb" style={{ width: 8, height: 8,  left:'35%', bottom:'12%', animationDuration:'7.5s', animationDelay:'4s'    }} />
                      {/* Ripple rings — only rendered when session is active */}
                      {isActive && (
                        <>
                          <div className="b-ripple" style={{ animationDelay:'0s'    }} />
                          <div className="b-ripple" style={{ animationDelay:'1.75s' }} />
                        </>
                      )}

                      {/* The breathing circle — unchanged */}
                      <div className="breathing-circle-wrapper">
                        <div className={`breathing-circle ${getCircleClass()}`}>
                          <div>
                            <span className="breathing-phase-label">
                              {isActive ? currentPhaseLabel : 'Ready'}
                            </span>
                            {isActive && (
                              <span className="breathing-count">{countdown}</span>
                            )}
                          </div>
                        </div>

                        {/* Session progress bar */}
                        {isActive && (
                          <>
                            <div className="breathing-timer-display">
                              {fmtTime(elapsed)} / {fmtTime(sessionSecs)}
                            </div>
                            <div className="breathing-session-bar-wrapper">
                              <div className="breathing-session-bar" style={{ width: `${progressPct}%` }} />
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* ── Controls ── */}
                    <div className="wellness-btn-group">
                      {!isActive ? (
                        <button className="wellness-btn wellness-btn-primary" onClick={handleStart}>
                          Start Breathing
                        </button>
                      ) : (
                        <button className="wellness-btn" onClick={handlePause}>
                          Pause
                        </button>
                      )}
                      {(isActive || elapsed > 0) && (
                        <button className="wellness-btn" onClick={handleReset}>Reset</button>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* How-to tips card */}
              <div className="wellness-card">
                <p style={{ color: '#003a4d', fontWeight: 700, marginBottom: 10, fontSize: 15 }}>
                  Tips for best results
                </p>
                <ul style={{ paddingLeft: 18, color: '#555', fontSize: 14, lineHeight: 1.8, marginBottom: 0 }}>
                  <li>Find a quiet, comfortable place to sit or lie down.</li>
                  <li>Breathe in through your nose and out slowly through your mouth.</li>
                  <li>If your mind wanders, gently bring attention back to the count.</li>
                  <li>Even 1–2 minutes can significantly lower your heart rate.</li>
                </ul>
              </div>

              {/* Link to other tools */}
              <div style={{ textAlign: 'center', marginTop: 32 }}>
                <p style={{ color: '#555', fontSize: 14, marginBottom: 12 }}>Explore more wellness tools:</p>
                <Link to="/wellness-quiz">
                  <button className="wellness-btn" style={{ marginRight: 8 }}>Take the Stress Quiz</button>
                </Link>
                <Link to="/guided-visualization">
                  <button className="wellness-btn">Guided Visualisation</button>
                </Link>
              </div>

            </div>
          </Grid>
        </Grid>
      </div>
    </Layout>
  )
}
